import StorageIcon from '@mui/icons-material/Storage';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import { WarehouseInventoryDetail } from '@/types/fulfill/inventory';

interface WarehouseInventoryPanelProps {
  id?: string;
  open: boolean;
  details: WarehouseInventoryDetail[];
  onRequestClose: () => void;
  onExited: () => void;
}

const COLUMN_TEMPLATE = '1.5fr 1fr 1fr 1fr';
const SECTION_PADDING_X = '1.5rem';

// Numeric columns are right-aligned with tabular figures so quantities line up
// and are easy to scan/compare across warehouses.
const NUMERIC_CELL_SX = {
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums',
};

const WarehouseInventoryPanel = ({
  id,
  open,
  details,
  onRequestClose,
  onExited,
}: WarehouseInventoryPanelProps) => {
  const totals = details.reduce(
    (acc, warehouse) => ({
      available: acc.available + warehouse.available,
      physical: acc.physical + warehouse.physical,
      inProcess: acc.inProcess + warehouse.inProcess,
    }),
    { available: 0, physical: 0, inProcess: 0 },
  );

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('.warehouse-detail-toggle')) return;
        onRequestClose();
      }}
    >
      <Collapse in={open} appear timeout={250} onExited={onExited} sx={{ width: '100%' }}>
        <Box id={id} role="region" sx={{ width: '100%', textAlign: 'left', my: '0.75rem' }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              px: SECTION_PADDING_X,
              py: '0.75rem',
              backgroundColor: (theme) => theme.palette.primary.lighter,
              borderTop: (theme) => `1px solid ${theme.palette.grey[200]}`,
              borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <StorageIcon color="primary" sx={{ fontSize: '1.35rem' }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: (theme) => theme.palette.secondary.main }}
            >
              Inventario por almacén
            </Typography>
          </Box>

          {details.length === 0 ? (
            <Box
              sx={{
                px: SECTION_PADDING_X,
                py: '1rem',
                backgroundColor: (theme) => theme.palette.common.white,
              }}
            >
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[600] }}>
                Este producto no tiene desglose por almacén disponible.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box
                display="grid"
                gridTemplateColumns={COLUMN_TEMPLATE}
                sx={{
                  px: SECTION_PADDING_X,
                  py: 0.75,
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: (theme) => theme.palette.secondary.main }}
                >
                  Almacén
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  Disponible
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  Físico
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  En proceso
                </Typography>
              </Box>

              {details.map((warehouse) => {
                const outOfStock = warehouse.available <= 0;
                return (
                  <Box
                    key={warehouse.warehouseId}
                    display="grid"
                    gridTemplateColumns={COLUMN_TEMPLATE}
                    sx={{
                      px: SECTION_PADDING_X,
                      py: 0.75,
                      backgroundColor: (theme) => theme.palette.common.white,
                      borderBottom: (theme) => `1px solid ${theme.palette.grey[100]}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => theme.palette.secondary.main }}
                    >
                      {warehouse.warehouseName}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {outOfStock ? (
                        <Chip
                          label="Sin stock"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            backgroundColor: (theme) => theme.palette.error.lighter,
                            color: (theme) => theme.palette.error.dark,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            ...NUMERIC_CELL_SX,
                            color: (theme) => theme.palette.secondary.main,
                          }}
                        >
                          {warehouse.available}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                    >
                      {warehouse.physical}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                    >
                      {warehouse.inProcess}
                    </Typography>
                  </Box>
                );
              })}

              <Box
                display="grid"
                gridTemplateColumns={COLUMN_TEMPLATE}
                sx={{
                  px: SECTION_PADDING_X,
                  py: 1.25,
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderTop: (theme) => `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: (theme) => theme.palette.secondary.main }}
                >
                  Totales
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  {totals.available}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  {totals.physical}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ ...NUMERIC_CELL_SX, color: (theme) => theme.palette.secondary.main }}
                >
                  {totals.inProcess}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </ClickAwayListener>
  );
};

export default WarehouseInventoryPanel;
